import type { OrgKey } from '@compass/client/types/org_key';
import { useRotateOrgKey } from '@hooks/useOrgKeys';
import { getErrorMessage } from '@utils/errorMessages';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface RotateOrgKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgKey: OrgKey;
  onRotated: (apiKey: string) => void;
}

export function RotateOrgKeyDialog({
  open,
  onOpenChange,
  orgKey,
  onRotated,
}: RotateOrgKeyDialogProps) {
  const rotateMutation = useRotateOrgKey();

  const handleRotate = () => {
    if (!orgKey.organization_id || !orgKey.org_key_id) return;
    rotateMutation.mutate(
      { organization_id: orgKey.organization_id, org_key_id: orgKey.org_key_id },
      {
        onSuccess: data => {
          onOpenChange(false);
          if (data.api_key) onRotated(data.api_key);
        },
        onError: err => {
          toast.error(getErrorMessage(err) || 'Failed to rotate API key');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rotate API Key</DialogTitle>
          <DialogDescription className="pt-2">
            Generate a new key for{' '}
            <span className="font-medium text-foreground">{orgKey.name}</span>. The current key
            stops working immediately.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={rotateMutation.isPending}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleRotate} disabled={rotateMutation.isPending}>
            {rotateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Rotating...
              </>
            ) : (
              'Rotate'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
